import React, { useState, useEffect } from 'react';
import { 
  XMarkIcon, 
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  MapPinIcon,
  BookOpenIcon,
  CheckIcon,
  PlusIcon,
  CalendarIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Session, Country } from '../../types/UniversityPortal';

interface University {
  id: number;
  name: string;
  description?: string;
  isActive: boolean;
}

interface Campus {
  id: number;
  name: string;
  city: string;
  country: string;
  universityId: number;
  universityName: string;
  isActive: boolean;
}

interface Course {
  id: number;
  name: string;
  code: string;
  universityId: number;
  universityName: string;
  isActive: boolean;
}

interface AddLeadAssociationModalProps {
  isOpen: boolean;
  onClose: () => void;
  leadId: string;
  leadName: string;
  onSuccess: () => void;
}

const AddLeadAssociationModal: React.FC<AddLeadAssociationModalProps> = ({
  isOpen,
  onClose,
  leadId,
  leadName,
  onSuccess
}) => {
  const [step, setStep] = useState(1);
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [selectedCampus, setSelectedCampus] = useState<Campus | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<Course | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [universities, setUniversities] = useState<University[]>([]);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [isActive, setIsActive] = useState<boolean>(true);

  // Search states
  const [sessionSearch, setSessionSearch] = useState('');
  const [universitySearch, setUniversitySearch] = useState('');
  const [campusSearch, setCampusSearch] = useState('');
  const [courseSearch, setCourseSearch] = useState('');
  const [countrySearch, setCountrySearch] = useState('');

  // Filtered data
  const [filteredSessions, setFilteredSessions] = useState<Session[]>([]);
  const [filteredUniversities, setFilteredUniversities] = useState<University[]>([]);
  const [filteredCampuses, setFilteredCampuses] = useState<Campus[]>([]);
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
      fetchUniversities();
      fetchCountries();
      resetForm();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedUniversity) {
      fetchCampuses(selectedUniversity.id);
    }
  }, [selectedUniversity]);

  useEffect(() => {
    if (selectedCampus) {
      fetchCourses(selectedCampus.universityId);
    }
  }, [selectedCampus]);

  // Filter sessions
  useEffect(() => {
    const filtered = sessions.filter(session =>
      session.name.toLowerCase().includes(sessionSearch.toLowerCase()) ||
      session.code.toLowerCase().includes(sessionSearch.toLowerCase())
    );
    setFilteredSessions(filtered);
  }, [sessions, sessionSearch]);

  // Filter universities
  useEffect(() => {
    const filtered = universities.filter(uni =>
      uni.name.toLowerCase().includes(universitySearch.toLowerCase()) ||
      (uni.description && uni.description.toLowerCase().includes(universitySearch.toLowerCase()))
    );
    setFilteredUniversities(filtered);
  }, [universities, universitySearch]);

  // Filter campuses
  useEffect(() => {
    const filtered = campuses.filter(campus =>
      campus.name.toLowerCase().includes(campusSearch.toLowerCase()) ||
      campus.city.toLowerCase().includes(campusSearch.toLowerCase()) ||
      campus.country.toLowerCase().includes(campusSearch.toLowerCase())
    );
    setFilteredCampuses(filtered);
  }, [campuses, campusSearch]);

  // Filter courses
  useEffect(() => {
    const filtered = courses.filter(course =>
      course.name.toLowerCase().includes(courseSearch.toLowerCase()) ||
      course.code.toLowerCase().includes(courseSearch.toLowerCase())
    );
    setFilteredCourses(filtered);
  }, [courses, courseSearch]);

  // Filter countries
  useEffect(() => {
    const filtered = countries.filter(country =>
      country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      (country.code && country.code.toLowerCase().includes(countrySearch.toLowerCase()))
    );
    setFilteredCountries(filtered);
  }, [countries, countrySearch]);

  const resetForm = () => {
    setStep(1);
    setSelectedSession(null);
    setSelectedUniversity(null);
    setSelectedCampus(null);
    setSelectedCourse(null);
    setSessionSearch('');
    setUniversitySearch('');
    setCampusSearch('');
    setCourseSearch('');
    setCountrySearch('');
    setIsActive(true);
    setError(null);
  };

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/university-portal/sessions?limit=1000');
      if (response.data.success) {
        setSessions(response.data.data || []);
      }
    } catch (err: any) {
      setError('Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const fetchUniversities = async () => {
    try {
      setLoading(true);
      const response = await api.get('/university-portal/universities?limit=1000');
      if (response.data.success) {
        setUniversities(response.data.data || []);
      }
    } catch (err: any) {
      setError('Failed to fetch universities');
    } finally {
      setLoading(false);
    }
  };

  const fetchCampuses = async (universityId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/university-portal/universities/${universityId}/campuses`);
      if (response.data.success) {
        setCampuses(response.data.data || []);
      }
    } catch (err: any) {
      setError('Failed to fetch campuses');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async (universityId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/university-portal/universities/${universityId}/courses`);
      if (response.data.success) {
        setCourses(response.data.data || []);
      }
    } catch (err: any) {
      setError('Failed to fetch courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/university-portal/countries');
      if (response.data.success) {
        setCountries(response.data.data || []);
      }
    } catch (err: any) {
      setError('Failed to fetch countries');
    } finally {
      setLoading(false);
    }
  };

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setStep(2);
  };

  const handleSessionSelect = (session: Session) => {
    setSelectedSession(session);
    setStep(3);
  };

  const handleUniversitySelect = (university: University) => {
    setSelectedUniversity(university);
    setSelectedCampus(null);
    setSelectedCourse(null);
    setStep(4);
  };

  const handleCampusSelect = (campus: Campus) => {
    setSelectedCampus(campus);
    setSelectedCourse(null);
    setStep(5);
  };

  const handleCourseSelect = (course: Course) => {
    setSelectedCourse(course);
  };

  const handleSubmit = async () => {
    if (!selectedUniversity) {
      setError('Please select a university');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const associationData = {
        leadId: parseInt(leadId),
        sessionId: selectedSession?.id || null,
        universityId: selectedUniversity.id,
        campusId: selectedCampus?.id || null,
        courseId: selectedCourse?.id || null,
        countryId: selectedCountry?.id || null,
        isActive: isActive
      };

      await api.post('/lead-associations', { associations: [associationData] });
      
      onSuccess();
      
      // Ask user if they want to add another association
      if (window.confirm('Association created successfully! Would you like to add another association for this lead?')) {
        // Reset form but keep modal open
        resetForm();
      } else {
        // Close modal
        onClose();
        resetForm();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create association');
    } finally {
      setSubmitting(false);
    }
  };

  const getAssociationType = () => {
    if (selectedCourse) return 'Course';
    if (selectedCampus) return 'Campus';
    return 'University';
  };

  const getAssociationPath = () => {
    let path = '';
    if (selectedSession) path += `${selectedSession.name} → `;
    path += selectedUniversity?.name || '';
    if (selectedCampus) path += ` → ${selectedCampus.name}`;
    if (selectedCourse) path += ` → ${selectedCourse.name}`;
    return path;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add University Association</h2>
            <p className="text-sm text-gray-600">For {leadName}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2, 3, 4, 5].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  step >= stepNumber
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step > stepNumber ? (
                    <CheckIcon className="h-5 w-5" />
                  ) : (
                    stepNumber
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  step >= stepNumber ? 'text-blue-600' : 'text-gray-500'
                }`}>
                  {stepNumber === 1 ? 'Country' : stepNumber === 2 ? 'Session' : stepNumber === 3 ? 'University' : stepNumber === 4 ? 'Campus' : 'Course'}
                </span>
                {stepNumber < 5 && (
                  <div className={`w-6 h-0.5 ml-4 ${
                    step > stepNumber ? 'bg-blue-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Step 1: Country Selection (Optional) */}
          {step === 1 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Country (Optional)</h3>
              <p className="text-sm text-gray-600 mb-4">
                You can optionally associate a country with this lead association, or skip this step.
              </p>
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search countries..."
                    value={countrySearch}
                    onChange={(e) => setCountrySearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : filteredCountries.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No countries found
                </div>
              ) : (
                <div className="divide-y divide-gray-200 max-h-60 overflow-y-auto">
                  <button
                    onClick={() => setStep(2)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                  >
                    <div className="flex items-center">
                      <GlobeAltIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <div className="font-medium text-gray-900">Skip Country</div>
                        <div className="text-sm text-gray-500">Continue without country selection</div>
                      </div>
                    </div>
                  </button>
                  {filteredCountries.map((country) => (
                    <button
                      key={country.id}
                      onClick={() => handleCountrySelect(country)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
                        selectedCountry?.id === country.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <GlobeAltIcon className="h-5 w-5 text-teal-600 mr-3" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{country.name}</div>
                          {country.code && (
                            <div className="text-sm text-gray-500">Code: {country.code}</div>
                          )}
                        </div>
                        {selectedCountry?.id === country.id && (
                          <CheckIcon className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selectedCountry && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Country selected: {selectedCountry.name} {selectedCountry.code && `(${selectedCountry.code})`}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Session Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select Session (Optional)</h3>
                <p className="text-sm text-gray-600 mb-4">Choose an academic session for this association</p>
              </div>

              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search sessions..."
                  value={sessionSearch}
                  onChange={(e) => setSessionSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading sessions...</span>
                  </div>
                ) : filteredSessions.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No sessions found
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    <button
                      onClick={() => setStep(3)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                    >
                      <div className="flex items-center">
                        <CalendarIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <div className="font-medium text-gray-900">Skip Session</div>
                          <div className="text-sm text-gray-500">Continue without session selection</div>
                        </div>
                      </div>
                    </button>
                    {filteredSessions.map((session) => (
                      <button
                        key={session.id}
                        onClick={() => handleSessionSelect(session)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                      >
                        <div className="flex items-center">
                          <CalendarIcon className="h-5 w-5 text-blue-500 mr-3" />
                          <div>
                            <div className="font-medium text-gray-900">{session.name}</div>
                            <div className="text-sm text-gray-500">
                              {session.code} • {session.startDate ? new Date(session.startDate).toLocaleDateString() : 'No start date'} - {session.endDate ? new Date(session.endDate).toLocaleDateString() : 'No end date'}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 3: University Selection */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select University</h3>
                <p className="text-sm text-gray-600 mb-4">Choose the university for this association</p>
              </div>

              {selectedCountry && (
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <div className="flex items-center">
                    <GlobeAltIcon className="h-4 w-4 text-teal-600 mr-2" />
                    <span className="text-sm font-medium text-gray-900">
                      Selected Country: {selectedCountry.name} {selectedCountry.code && `(${selectedCountry.code})`}
                    </span>
                  </div>
                </div>
              )}
              
              {selectedSession && (
                <div className="bg-blue-50 p-3 rounded-lg mb-4">
                  <div className="flex items-center">
                    <CalendarIcon className="h-4 w-4 text-blue-500 mr-2" />
                    <span className="text-sm font-medium text-gray-900">
                      Selected Session: {selectedSession.name} ({selectedSession.code})
                    </span>
                  </div>
                </div>
              )}

              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search universities..."
                  value={universitySearch}
                  onChange={(e) => setUniversitySearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading universities...</span>
                  </div>
                ) : filteredUniversities.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No universities found
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredUniversities.map((university) => (
                      <button
                        key={university.id}
                        onClick={() => handleUniversitySelect(university)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                      >
                        <div className="flex items-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-green-500 mr-3" />
                          <div>
                            <div className="font-medium text-gray-900">{university.name}</div>
                            {university.description && (
                              <div className="text-sm text-gray-500">{university.description}</div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Campus Selection */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select Campus (Optional)</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Choose a specific campus or skip to select a course
                </p>
              </div>

              <div className="bg-blue-50 p-3 rounded-lg mb-4">
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-4 w-4 text-green-500 mr-2" />
                  <span className="text-sm font-medium text-gray-900">
                    Selected: {selectedUniversity?.name}
                  </span>
                </div>
              </div>

              <div className="flex space-x-2 mb-4">
                <button
                  onClick={() => setStep(5)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Skip Campus
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Creating...' : 'Finish Association'}
                </button>
              </div>

              {/* Status Selection */}
              <div className="mt-4 p-4 border border-gray-200 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Association Status
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="isActive-campus"
                      value="true"
                      checked={isActive === true}
                      onChange={() => setIsActive(true)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="isActive-campus"
                      value="false"
                      checked={isActive === false}
                      onChange={() => setIsActive(false)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Select whether this association should be active or inactive
                </p>
              </div>

              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search campuses..."
                  value={campusSearch}
                  onChange={(e) => setCampusSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-lg">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-gray-600">Loading campuses...</span>
                  </div>
                ) : filteredCampuses.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No campuses found
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredCampuses.map((campus) => (
                      <button
                        key={campus.id}
                        onClick={() => handleCampusSelect(campus)}
                        className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                      >
                        <div className="flex items-center">
                          <MapPinIcon className="h-5 w-5 text-purple-500 mr-3" />
                          <div>
                            <div className="font-medium text-gray-900">{campus.name}</div>
                            <div className="text-sm text-gray-500">
                              {campus.city}, {campus.country}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Course Selection */}
          {step === 5 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Select Course (Optional)</h3>
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={courseSearch}
                    onChange={(e) => setCourseSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : filteredCourses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No courses found
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {filteredCourses.map((course) => (
                    <button
                      key={course.id}
                      onClick={() => handleCourseSelect(course)}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none ${
                        selectedCourse?.id === course.id ? 'bg-blue-50' : ''
                      }`}
                    >
                      <div className="flex items-center">
                        <BookOpenIcon className="h-5 w-5 text-orange-600 mr-3" />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{course.name}</div>
                          {course.code && (
                            <div className="text-sm text-gray-500">Code: {course.code}</div>
                          )}
                        </div>
                        {selectedCourse?.id === course.id && (
                          <CheckIcon className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selectedCourse && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    Course selected: {selectedCourse.name} ({selectedCourse.code})
                  </p>
                </div>
              )}
              
              {/* Status Selection */}
              <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Association Status
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="isActive"
                      value="true"
                      checked={isActive === true}
                      onChange={() => setIsActive(true)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="isActive"
                      value="false"
                      checked={isActive === false}
                      onChange={() => setIsActive(false)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-700">Inactive</span>
                  </label>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Select whether this association should be active or inactive
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <div className="flex space-x-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
            )}
            
            {step >= 3 && selectedUniversity && (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Creating...' : 'Create Association'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddLeadAssociationModal;
